import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Modal, Select, Table, Typography, Upload, message } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  DEPOSIT_INSURANCE_AUDIT_BIZ_TYPE,
  TEMPLATE_FILE_NAME,
  buildTemplateSheet,
  getWeekPeriods,
  isInProgress,
  statusLabel,
} from './depositInsuranceUploadUtils';
import { GET_DEPOSIT_INSURANCE_RESULT_FILE_URL, GET_DEPOSIT_INSURANCE_UPLOAD_TASKS } from './graphql/queries';
import { SUBMIT_DEPOSIT_INSURANCE_UPLOAD } from './graphql/mutations';
import type {
  DepositInsuranceResultFileUrlResponse,
  DepositInsuranceUploadTask,
  DepositInsuranceUploadTasksResponse,
} from './graphql/types';

interface Props {
  visible: boolean;
  onClose: () => void;
}

const POLL_INTERVAL_MS = 10_000;

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      const comma = typeof dataUrl === 'string' ? dataUrl.indexOf(',') : -1;
      if (typeof dataUrl !== 'string' || comma === -1) {
        reject(new Error('读取文件失败'));
        return;
      }
      resolve(dataUrl.slice(comma + 1));
    };
    reader.onerror = () => reject(new Error('读取文件失败'));
    reader.readAsDataURL(file);
  });

const fmtDate = (v?: string | null) => (v ? v.slice(0, 10) : '-');
const fmtDateTime = (v?: string | null) => (v ? new Date(v).toLocaleString() : '-');
const fmtYuan = (cents?: number | null) => (cents == null ? '-' : (Number(cents) / 100).toFixed(2));

export const DepositInsuranceUploadModal: React.FC<Props> = ({ visible, onClose }) => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const weekPeriods = useMemo(() => getWeekPeriods(), []);
  const [periodIdx, setPeriodIdx] = useState<number | undefined>(undefined);
  const [uploading, setUploading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const { data, loading, refetch, startPolling, stopPolling } = useQuery<DepositInsuranceUploadTasksResponse>(
    GET_DEPOSIT_INSURANCE_UPLOAD_TASKS,
    {
      variables: { offset: (pagination.current - 1) * pagination.pageSize, limit: pagination.pageSize },
      fetchPolicy: 'cache-and-network',
      skip: !visible,
    },
  );
  const [submitUpload] = useMutation(SUBMIT_DEPOSIT_INSURANCE_UPLOAD);

  const items = data?.depositInsuranceUploadTasks?.items ?? [];
  const total = data?.depositInsuranceUploadTasks?.total ?? 0;
  const hasInProgress = items.some((it) => isInProgress(it.status));

  useEffect(() => {
    if (visible && hasInProgress) {
      startPolling(POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [visible, hasInProgress, startPolling, stopPolling]);

  const handleDownloadTemplate = () => {
    const worksheet = XLSX.utils.aoa_to_sheet(buildTemplateSheet());
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', TEMPLATE_FILE_NAME);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleUpload = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      message.error('仅支持 .xlsx 文件');
      return false;
    }
    if (periodIdx === undefined) {
      message.error('请先选择账期');
      return false;
    }
    const period = weekPeriods[periodIdx];
    setUploading(true);
    try {
      const fileBase64 = await readFileAsBase64(file);
      await submitUpload({
        variables: {
          periodStart: period.periodStart,
          periodEnd: period.periodEnd,
          fileBase64,
          fileName: file.name,
        },
      });
      message.success('已提交，请等待审核');
      setPagination((p) => ({ ...p, current: 1 }));
      await refetch({ offset: 0, limit: pagination.pageSize });
    } catch (e: any) {
      message.error(e?.message || '提交失败，请重试');
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleViewDetail = async (taskId: number | string) => {
    try {
      const res = await client.query<DepositInsuranceResultFileUrlResponse>({
        query: GET_DEPOSIT_INSURANCE_RESULT_FILE_URL,
        variables: { taskId },
        fetchPolicy: 'network-only',
      });
      const url = res.data?.depositInsuranceResultFileUrl;
      if (!url) {
        message.warning('暂无明细文件');
        return;
      }
      window.open(url, '_blank');
    } catch (e: any) {
      message.error(e?.message || '获取明细失败');
    }
  };

  const columns: ColumnsType<DepositInsuranceUploadTask> = [
    { title: '任务ID', dataIndex: 'id', key: 'id', width: 90 },
    { title: '文件名', dataIndex: 'sourceFileName', key: 'sourceFileName', ellipsis: true },
    {
      title: '账期',
      key: 'period',
      width: 190,
      render: (_, r) => `${fmtDate(r.periodStart)} ~ ${fmtDate(r.periodEnd)}`,
    },
    { title: '提交人', dataIndex: 'submitterName', key: 'submitterName', width: 100 },
    { title: '提交时间', dataIndex: 'createdAt', key: 'createdAt', width: 170, render: fmtDateTime },
    { title: '总笔数', dataIndex: 'rowCount', key: 'rowCount', width: 80 },
    {
      title: '总金额(元)',
      dataIndex: 'totalAmountCents',
      key: 'totalAmountCents',
      width: 110,
      render: fmtYuan,
    },
    { title: '成功数', dataIndex: 'successCount', key: 'successCount', width: 80 },
    { title: '失败数', dataIndex: 'failCount', key: 'failCount', width: 80 },
    {
      title: '状态',
      key: 'status',
      width: 130,
      render: (_, r) => statusLabel(r.status, r.failureReason),
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      render: (_, r) => (
        <div className="flex gap-2">
          {r.hasResultFile && (
            <Button type="link" size="small" onClick={() => handleViewDetail(r.id)}>
              查看明细
            </Button>
          )}
          <Button
            type="link"
            size="small"
            onClick={() =>
              // 审核页目前不支持 URL 驱动筛选，仅跳转并通过 state 携带 bizType
              navigate('/settlement/adjustment-review', {
                state: { bizType: DEPOSIT_INSURANCE_AUDIT_BIZ_TYPE },
              })
            }
          >
            查看审核任务
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title="保证金/保险入账"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1100}
      destroyOnClose
    >
      <Card title="上传设置" size="small" className="mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <Select
            style={{ width: 300 }}
            placeholder="请选择账期"
            value={periodIdx}
            onChange={setPeriodIdx}
            options={weekPeriods.map((p, i) => ({ label: p.label, value: i }))}
          />
          <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
            下载模板
          </Button>
          <Upload accept=".xlsx" showUploadList={false} beforeUpload={handleUpload} disabled={uploading}>
            <Button type="primary" icon={<UploadOutlined />} loading={uploading}>
              上传明细
            </Button>
          </Upload>
        </div>
        <Typography.Paragraph type="secondary" className="mt-3 mb-0">
          模板共 3 列：DSP Name、费用类型（Deposit / Insurance）、金额（&gt;0 且 ≤20,000.00，最多 2 位小数）；
          单文件最多 5,000 行；同一 DSP 可同时有 Deposit 和 Insurance 各一行。
        </Typography.Paragraph>
      </Card>
      <Table<DepositInsuranceUploadTask>
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={items}
        scroll={{ x: 1100 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total,
          showSizeChanger: false,
          onChange: (current, pageSize) => setPagination({ current, pageSize }),
        }}
      />
    </Modal>
  );
};

export default DepositInsuranceUploadModal;
